// src/pages/AddTransactionPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import LoadingSpinner from '../components/LoadingSpinner';

const AddTransactionPage = () => {
    const navigate = useNavigate();
    const { loading, funMoneyData, funMoneySettings, addFunMoneyTransaction } = useAppContext();

    const [transaction, setTransaction] = useState({
        person: 'you', // Default to 'you'
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (loading || !funMoneyData || !funMoneySettings) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner size="large" message="Loading..." />
            </div>
        );
    }

    const categories = funMoneySettings.categories || [
        'Dining Out',
        'Entertainment',
        'Shopping',
        'Hobbies',
        'Other'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTransaction({
            ...transaction,
            [name]: name === 'amount' ? value : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);

        try {
            // Validate form
            if (!transaction.description) {
                throw new Error('Please enter a description');
            }

            if (!transaction.amount || isNaN(parseFloat(transaction.amount))) {
                throw new Error('Please enter a valid amount');
            }

            // Convert amount to negative for spending
            const finalTransaction = {
                ...transaction,
                amount: parseFloat(transaction.amount) * -1 // Make it negative to represent spending
            };

            await addFunMoneyTransaction(finalTransaction);

            // Navigate to fun money page after successful submission
            navigate('/fun-money');
        } catch (err) {
            setError(err.message || 'Failed to add transaction');
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-3xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Add Fun Money Transaction</h1>
                <Link
                    to="/fun-money"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                    Cancel
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                            Person
                        </label>
                        <div className="flex">
                            <button
                                type="button"
                                className={`flex-1 py-2 px-4 text-center ${transaction.person === 'you'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    } rounded-l`}
                                onClick={() => setTransaction({ ...transaction, person: 'you' })}
                            >
                                You
                            </button>
                            <button
                                type="button"
                                className={`flex-1 py-2 px-4 text-center ${transaction.person === 'spouse'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    } rounded-r`}
                                onClick={() => setTransaction({ ...transaction, person: 'spouse' })}
                            >
                                {funMoneyData.spouse.name}
                            </button>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                            Description
                        </label>
                        <input
                            type="text"
                            id="description"
                            name="description"
                            value={transaction.description}
                            onChange={handleInputChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            placeholder="Coffee shop, movie tickets, etc."
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="amount">
                            Amount ($)
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500">$</span>
                            </div>
                            <input
                                type="number"
                                id="amount"
                                name="amount"
                                value={transaction.amount}
                                onChange={handleInputChange}
                                className="shadow appearance-none border rounded w-full py-2 px-3 pl-7 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                placeholder="12.50"
                                step="0.01"
                                min="0.01"
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
                            Category
                        </label>
                        <select
                            id="category"
                            name="category"
                            value={transaction.category}
                            onChange={handleInputChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        >
                            <option value="">Select a category</option>
                            {categories.map((category) => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
                            Date
                        </label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            value={transaction.date}
                            onChange={handleInputChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            required
                        />
                    </div>

                    <div className="flex justify-between">
                        <Link
                            to="/fun-money"
                            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:shadow-outline"
                        >
                            Cancel
                        </Link>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:shadow-outline disabled:bg-gray-400"
                        >
                            {submitting ? 'Adding...' : 'Add Transaction'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-gray-100 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-gray-700 mb-2">Current Fun Money Balances:</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600">You</p>
                        <p className="font-bold">${funMoneyData.you.current_balance.toFixed(2)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">{funMoneyData.spouse.name}</p>
                        <p className="font-bold">${funMoneyData.spouse.current_balance.toFixed(2)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTransactionPage;